export default function Realms() {
  const realms = [
    {
      name: 'Mystical Forest',
      tagline: 'Immersive environment',
      features: ['Growth, grounding and creativity', 'Nature-inspired movement flow'],
      color: 'from-green-400 to-emerald-600',
      icon: 'ri-plant-line',
      image: 'https://public.readdy.ai/ai/img_res/cd2f0bd6bf5f140c366b9e4b3159afb4.jpg'
    },
    {
      name: 'Volcanic Arena',
      tagline: 'Multi-sensory experience',
      features: ['Real-time performance feedback', 'High-energy movement expression'],
      color: 'from-orange-500 to-red-600',
      icon: 'ri-fire-line',
      image: 'https://readdy.ai/api/search-image?query=Volcanic%20arena%20digital%20environment%20with%20flowing%20lava%2C%20glowing%20orange%20and%20red%20particles%2C%20dramatic%20rock%20formations%2C%20intense%20lighting%2C%20heat%20shimmer%20effects%2C%20Unreal%20Engine%205%20rendered%2C%20dark%20background%20with%20vibrant%20fire%20accents%2C%20powerful%20and%20energetic%20atmosphere%2C%20immersive%20virtual%20space&width=800&height=600&seq=realm-volcano-001&orientation=landscape'
    },
    {
      name: 'Shifting Canyon',
      tagline: 'AI-guided missions',
      features: ['Creativity and adaptability', 'Dynamic environment challenges'],
      color: 'from-amber-400 to-yellow-600',
      icon: 'ri-landscape-line',
      image: 'https://readdy.ai/api/search-image?query=Shifting%20canyon%20virtual%20environment%20with%20moving%20rock%20formations%2C%20golden%20amber%20lighting%2C%20dynamic%20pathways%2C%20floating%20platforms%2C%20dust%20particles%2C%20cinematic%20desert%20atmosphere%2C%20Unreal%20Engine%205%20graphics%2C%20dark%20background%20with%20warm%20yellow%20accents%2C%20adaptive%20digital%20landscape%2C%20mysterious%20and%20intriguing%20mood&width=1200&height=600&seq=realm-canyon-001&orientation=landscape'
    },
    {
      name: 'Futuristic Forge',
      tagline: 'Inclusivity and personal growth',
      features: ['Precision and control', 'Tech-enhanced collaboration'],
      color: 'from-cyan-400 to-blue-600',
      icon: 'ri-tools-line',
      image: 'https://readdy.ai/api/search-image?query=Futuristic%20forge%20digital%20workshop%20with%20holographic%20interfaces%2C%20glowing%20cyan%20circuits%2C%20metallic%20surfaces%2C%20precision%20tools%2C%20high-tech%20machinery%2C%20neon%20blue%20lighting%2C%20Unreal%20Engine%205%20quality%2C%20dark%20background%20with%20vibrant%20cyan%20accents%2C%20advanced%20technology%20environment%2C%20innovative%20and%20precise%20atmosphere&width=800&height=600&seq=realm-forge-001&orientation=landscape'
    },
    {
      name: 'Submerged Temple',
      tagline: 'Movement meditation',
      features: ['Wellness and harmony', 'Mindful body awareness'],
      color: 'from-teal-400 to-cyan-600',
      icon: 'ri-water-flash-line',
      image: 'https://readdy.ai/api/search-image?query=Underwater%20submerged%20temple%20virtual%20environment%20with%20ancient%20ruins%2C%20flowing%20water%20effects%2C%20glowing%20teal%20particles%2C%20peaceful%20aquatic%20atmosphere%2C%20gentle%20light%20rays%2C%20meditation%20space%2C%20Unreal%20Engine%205%20rendered%2C%20dark%20background%20with%20soft%20cyan%20accents%2C%20serene%20and%20calming%20mood%2C%20immersive%20wellness%20sanctuary&width=800&height=600&seq=realm-temple-001&orientation=landscape'
    }
  ];

  return (
    <section id="experience" className="py-32 bg-black relative overflow-hidden">
      {/* Animated Particle Field */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: ['#00FF88', '#FF6B35', '#00D9FF', '#FFB627', '#B24BF3'][Math.floor(Math.random() * 5)],
              opacity: 0.2 + Math.random() * 0.3,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <h2 className="font-serif text-5xl md:text-7xl font-bold text-center mb-8 bg-gradient-to-r from-green-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent">
          More Than a Workshop — A Multi-Sensory Journey
        </h2>
        
        <p className="text-gray-300 text-xl text-center max-w-4xl mx-auto mb-20 leading-relaxed">
          Participants journey through immersive virtual realms powered by Unreal Engine 5 — 
          blending movement, storytelling and wellness.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Large Cards - Top Row */}
          {realms.slice(0, 2).map((realm, index) => (
            <div 
              key={index}
              className="group relative rounded-3xl overflow-hidden h-[500px] cursor-pointer border-2 border-transparent hover:shadow-2xl transition-all duration-500"
              style={{
                borderImage: `linear-gradient(135deg, ${realm.color.split(' ')[1]}, ${realm.color.split(' ')[2]}) 1`
              }}
            >
              <div className="absolute inset-0">
                <img 
                  src={realm.image}
                  alt={realm.name}
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent"></div>
              </div>

              <div className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center opacity-50">
                <i className={`${realm.icon} text-5xl text-white`}></i>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-10">
                <h3 className="text-4xl font-bold text-white mb-3 drop-shadow-lg">
                  {realm.name}
                </h3>
                <p className={`text-sm font-semibold uppercase tracking-wider mb-6 bg-gradient-to-r ${realm.color} bg-clip-text text-transparent`}>
                  {realm.tagline}
                </p>
                <ul className="space-y-3">
                  {realm.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-white text-lg">
                      <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${realm.color}`}></div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}

          {/* Wide Card - Middle Row */}
          <div 
            className="group relative rounded-3xl overflow-hidden h-[500px] md:col-span-2 cursor-pointer border-2 border-transparent hover:shadow-2xl transition-all duration-500"
            style={{
              borderImage: `linear-gradient(135deg, ${realms[2].color.split(' ')[1]}, ${realms[2].color.split(' ')[2]}) 1`
            }}
          >
            <div className="absolute inset-0">
              <img 
                src={realms[2].image}
                alt={realms[2].name}
                className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent"></div>
            </div>

            <div className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center opacity-50">
              <i className={`${realms[2].icon} text-5xl text-white`}></i>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-10">
              <h3 className="text-4xl font-bold text-white mb-3 drop-shadow-lg">
                {realms[2].name}
              </h3>
              <p className={`text-sm font-semibold uppercase tracking-wider mb-6 bg-gradient-to-r ${realms[2].color} bg-clip-text text-transparent`}>
                {realms[2].tagline}
              </p>
              <ul className="space-y-3">
                {realms[2].features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-white text-lg">
                    <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${realms[2].color}`}></div>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Medium Cards - Bottom Row */}
          {realms.slice(3, 5).map((realm, index) => (
            <div 
              key={index + 3}
              className="group relative rounded-3xl overflow-hidden h-[500px] cursor-pointer border-2 border-transparent hover:shadow-2xl transition-all duration-500"
              style={{
                borderImage: `linear-gradient(135deg, ${realm.color.split(' ')[1]}, ${realm.color.split(' ')[2]}) 1`
              }}
            >
              <div className="absolute inset-0">
                <img 
                  src={realm.image}
                  alt={realm.name}
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent"></div>
              </div>

              <div className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center opacity-50">
                <i className={`${realm.icon} text-5xl text-white`}></i>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-10">
                <h3 className="text-4xl font-bold text-white mb-3 drop-shadow-lg">
                  {realm.name}
                </h3>
                <p className={`text-sm font-semibold uppercase tracking-wider mb-6 bg-gradient-to-r ${realm.color} bg-clip-text text-transparent`}>
                  {realm.tagline}
                </p>
                <ul className="space-y-3">
                  {realm.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-white text-lg">
                      <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${realm.color}`}></div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <a 
            href="#contact" 
            className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full text-white font-bold text-lg hover:shadow-2xl hover:shadow-cyan-500/50 transition-all hover:-translate-y-1 cursor-pointer whitespace-nowrap"
          >
            Experience The Five Realms
            <i className="ri-arrow-right-line text-xl"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
